import CustomTooltip from '../../../shared/CustomTooltip';
import {
    Checkbox,
    Grid,
    InputLabel,
    List,
    ListItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import UserRole from '../../../../shared/AuthRoles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    useGetCompanyGuestsInfoQuery,
    useGetGuestProfileQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import GuestPublicProfileRow from '../../Reviews/GuestPublicProfileRow';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BusinessIcon from '@mui/icons-material/Business';
import FormTextField from '../../../shared/FormTextField';
import {
    Controller,
    useFormContext,
    useFormState,
    useWatch,
} from 'react-hook-form';
import FormSelect from '../../../shared/FormSelect';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import ProfileSectionCard from '../ProfileSectionCard';
import ProfileReadOnlyField, {
    companyCardAccessLabel,
    formatCompanyAddressSingleLine,
    ProfileDisabledFieldWrap,
    ProfileShellFormCell,
    ProfileStatusDot,
    ProfileStatusIndicator,
} from '../ProfileReadOnlyField';
import { useEditProfileNavigation } from '../useEditProfileNavigation';
import { useProfileInlineUx } from '../ProfileInlineUxContext';

interface GuestCompanyDetailsProps {
    isEditing: boolean;
    modernProfileView?: boolean;
    contractorCompany?: boolean;
}

export default function GuestCompanyDetails({
    isEditing,
    modernProfileView = false,
    contractorCompany = false,
}: GuestCompanyDetailsProps) {
    const { control, reset, getValues } = useFormContext();
    const { isDirty } = useFormState({ control });
    const loggedInUser = UserRole();
    const canViewCompanyReviews =
        loggedInUser?.role === 'guest_admin' ||
        loggedInUser?.role === 'guest_department_admin';

    const { data: companyGuestInfo } = useGetCompanyGuestsInfoQuery(
        `/guests/${loggedInUser?.company}`,
    );
    const { data: guestCompanyProfile } = useGetGuestProfileQuery(
        loggedInUser?.company,
        {
            skip: !canViewCompanyReviews || !loggedInUser?.company,
        },
    );

    const [getCompanyDetails, setGetCompanyDetails] =
        useState<any>(companyGuestInfo);

    const companyW = useWatch({ control, name: 'company' }) as
        | Record<string, any>
        | undefined;

    const mergedCompany = useMemo(
        () => ({
            ...(getCompanyDetails && typeof getCompanyDetails === 'object'
                ? getCompanyDetails
                : {}),
            ...(companyW && typeof companyW === 'object' ? companyW : {}),
        }),
        [getCompanyDetails, companyW],
    );

    const hydratedRef = useRef(false);

    const isCompany = loggedInUser?.role === 'guest_company';
    const isCompanyAdmin =
        loggedInUser?.role === 'guest_admin' ||
        loggedInUser?.role === 'guest_company_admin';

    const canEditCompanyDetails = isEditing && isCompany && !isCompanyAdmin;

    const canEditMultipleDomains =
        isEditing &&
        (loggedInUser?.role === 'guest_admin' ||
            loggedInUser?.role === 'guest_company_admin' ||
            loggedInUser?.role === 'guest_company');

    const { canShowEdit } = useEditProfileNavigation(contractorCompany);
    /** guest_user, team/department admins, and view-only accounts cannot change company details here. */
    const canShowCompanyInlineEdit =
        canShowEdit &&
        loggedInUser?.role !== 'guest_user' &&
        loggedInUser?.role !== 'guest_department_admin' &&
        !loggedInUser?.viewOnly;
    const {
        suspendCompanyResets,
        inlineEditSection,
        beginInlineEdit,
        cancelInlineEdit,
        saveInlineEdit,
    } = useProfileInlineUx();

    useEffect(() => {
        setGetCompanyDetails(companyGuestInfo);

        if (!companyGuestInfo) return;

        if (suspendCompanyResets) return;

        if (!hydratedRef.current) {
            reset(
                { ...getValues(), company: companyGuestInfo },
                { keepDirtyValues: true, keepErrors: true },
            );
            hydratedRef.current = true;
            return;
        }

        if (!isEditing) {
            reset(
                { ...getValues(), company: companyGuestInfo },
                { keepDirtyValues: true, keepErrors: true },
            );
        }
    }, [companyGuestInfo, isEditing, reset, getValues, suspendCompanyResets]);

    const simplifiedAddress = `${getCompanyDetails?.streetAddress} ${getCompanyDetails?.suburb} ${getCompanyDetails?.postcode} ${getCompanyDetails?.state} ${getCompanyDetails?.countryCode}`;

    const shell = Boolean(modernProfileView);
    const readOnlyShell = shell && !isEditing;

    const guestCompanyReadOnly = (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ rowGap: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Company'>
                    {mergedCompany?.name}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Address'>
                    {formatCompanyAddressSingleLine(mergedCompany)}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Phone'>
                    {mergedCompany?.phone}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Primary Contact'>
                    {mergedCompany?.primaryContact}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Notification Email'>
                    {mergedCompany?.account?.email}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField
                    label='Allow multiple domains'
                    labelAdornment={
                        <CustomTooltip
                            title={
                                <Typography sx={{ fontFamily: 'Open Sans' }}>
                                    <List>
                                        <ListItem>
                                            If selected, users within this
                                            company can grant access to 7DAY
                                            using email addresses that are not
                                            part of their organisation (for
                                            example, personal email accounts).
                                            If not selected, only email
                                            addresses belonging to the
                                            organisation may be added.
                                        </ListItem>
                                    </List>
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    }
                >
                    {mergedCompany?.allowsMultipleDomains ? 'Yes' : 'No'}
                </ProfileReadOnlyField>
            </Grid>
            {isCompany && (
                <>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileReadOnlyField
                            label='Status'
                            labelAdornment={
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    Enabled: Users can access
                                                    the site and perform actions
                                                    based on their level of
                                                    access
                                                </ListItem>
                                                <ListItem>
                                                    Disabled: Users will not be
                                                    able to access the site
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            }
                        >
                            <ProfileStatusIndicator
                                active={mergedCompany?.isActive}
                            />
                        </ProfileReadOnlyField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileReadOnlyField
                            label='Access'
                            labelAdornment={
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    Master Account:
                                                    Add/Edit/View Users
                                                </ListItem>
                                                <ListItem>
                                                    Admin: Add/Edit/View
                                                    Listings, Users and
                                                    Reservations
                                                </ListItem>
                                                <ListItem>
                                                    User: Add/Edit/View
                                                    Reservations for themselves
                                                    & view listings
                                                </ListItem>
                                                <ListItem>
                                                    View Only User: View
                                                    listings & view reservations
                                                    made for them
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            }
                        >
                            {companyCardAccessLabel(loggedInUser)}
                        </ProfileReadOnlyField>
                    </Grid>
                </>
            )}
        </Grid>
    );

    const guestCompanyShellEdit = (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ rowGap: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Company'>
                    <ProfileDisabledFieldWrap disabled>
                        <FormTextField
                            name='company.name'
                            control={control}
                            disabled
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Address'>
                    {canEditCompanyDetails ? (
                        <Stack spacing={1}>
                            <FormTextField
                                name='company.streetAddress'
                                control={control}
                                size='small'
                                fullWidth
                            />
                            <FormTextField
                                name='company.suburb'
                                control={control}
                                size='small'
                                fullWidth
                            />
                            <FormTextField
                                type='number'
                                name='company.postcode'
                                control={control}
                                size='small'
                                fullWidth
                            />
                            <FormAutocomplete
                                name='company.state'
                                control={control}
                                size='small'
                                options={[
                                    { value: 'NSW', label: 'NSW' },
                                    { value: 'QLD', label: 'QLD' },
                                    { value: 'VIC', label: 'VIC' },
                                    { value: 'SA', label: 'SA' },
                                    { value: 'WA', label: 'WA' },
                                    { value: 'NT', label: 'NT' },
                                    { value: 'TAS', label: 'TAS' },
                                    { value: 'ACT', label: 'ACT' },
                                ]}
                            />
                            <FormAutocomplete
                                name='company.countryCode'
                                control={control}
                                size='small'
                                options={[{ value: 'AU', label: 'Australia' }]}
                            />
                        </Stack>
                    ) : (
                        <ProfileDisabledFieldWrap
                            disabled={!canEditCompanyDetails}
                        >
                            <TextField
                                size='small'
                                fullWidth
                                disabled
                                value={
                                    formatCompanyAddressSingleLine(
                                        mergedCompany,
                                    ) || '—'
                                }
                            />
                        </ProfileDisabledFieldWrap>
                    )}
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Phone'>
                    <ProfileDisabledFieldWrap disabled={!canEditCompanyDetails}>
                        <FormTextField
                            name='company.phone'
                            control={control}
                            disabled={!canEditCompanyDetails}
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Primary Contact'>
                    <ProfileDisabledFieldWrap disabled={!canEditCompanyDetails}>
                        <FormTextField
                            name='company.primaryContact'
                            control={control}
                            disabled={!canEditCompanyDetails}
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Email'>
                    <ProfileDisabledFieldWrap disabled>
                        <FormTextField
                            name='company.account.email'
                            control={control}
                            disabled
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell
                    label='Allow multiple domains'
                    labelAdornment={
                        <CustomTooltip
                            title={
                                <Typography sx={{ fontFamily: 'Open Sans' }}>
                                    <List>
                                        <ListItem>
                                            If selected, users within this
                                            company can grant access to 7DAY
                                            using email addresses that are not
                                            part of their organisation (for
                                            example, personal email accounts).
                                            If not selected, only email
                                            addresses belonging to the
                                            organisation may be added.
                                        </ListItem>
                                    </List>
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    }
                >
                    <Controller
                        name='company.allowsMultipleDomains'
                        control={control}
                        render={({ field }) => (
                            <ProfileDisabledFieldWrap
                                disabled={!canEditMultipleDomains}
                            >
                                <Checkbox
                                    checked={!!field.value}
                                    onChange={(e) =>
                                        field.onChange(e.target.checked)
                                    }
                                    disabled={!canEditMultipleDomains}
                                    sx={{ p: 0 }}
                                />
                            </ProfileDisabledFieldWrap>
                        )}
                    />
                </ProfileShellFormCell>
            </Grid>
            {isCompany && (
                <>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileShellFormCell
                            label='Status'
                            labelAdornment={
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    Enabled: Users can access
                                                    the site and perform actions
                                                    based on their level of
                                                    access
                                                </ListItem>
                                                <ListItem>
                                                    Disabled: Users will not be
                                                    able to access the site
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            }
                        >
                            <ProfileDisabledFieldWrap disabled>
                                <FormSelect
                                    name='company.isActive'
                                    control={control}
                                    size='small'
                                    fullWidth
                                    options={[
                                        {
                                            value: true,
                                            label: 'Enabled',
                                            iconLeft: (
                                                <ProfileStatusDot enabled />
                                            ),
                                        },
                                        {
                                            value: false,
                                            label: 'Disabled',
                                            iconLeft: (
                                                <ProfileStatusDot
                                                    enabled={false}
                                                />
                                            ),
                                        },
                                    ]}
                                    disabled
                                />
                            </ProfileDisabledFieldWrap>
                        </ProfileShellFormCell>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileShellFormCell
                            label='Access'
                            labelAdornment={
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    Master Account:
                                                    Add/Edit/View Users
                                                </ListItem>
                                                <ListItem>
                                                    Admin: Add/Edit/View
                                                    Listings, Users and
                                                    Reservations
                                                </ListItem>
                                                <ListItem>
                                                    User: Add/Edit/View
                                                    Reservations for themselves
                                                    & view listings
                                                </ListItem>
                                                <ListItem>
                                                    View Only User: View
                                                    listings & view reservations
                                                    made for them
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            }
                        >
                            <TextField
                                size='small'
                                fullWidth
                                disabled
                                value={companyCardAccessLabel(loggedInUser)}
                            />
                        </ProfileShellFormCell>
                    </Grid>
                </>
            )}
        </Grid>
    );

    const companyFieldsGrid = (
        <Grid container columnSpacing='50px' rowSpacing='10px'>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Company</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='company.name'
                        control={control}
                        disabled
                    />
                </Grid>
            </Grid>

            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Phone</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='company.phone'
                        control={control}
                        disabled={!canEditCompanyDetails}
                    />
                </Grid>
            </Grid>

            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Primary Contact</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='company.primaryContact'
                        control={control}
                        disabled={!canEditCompanyDetails}
                    />
                </Grid>
            </Grid>

            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Email</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='company.account.email'
                        control={control}
                        disabled
                    />
                </Grid>
            </Grid>

            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <Stack direction='row' gap='5px' alignItems='center'>
                        <InputLabel>Allow Multiple Domains</InputLabel>
                        <CustomTooltip
                            title={
                                <Typography sx={{ fontFamily: 'Open Sans' }}>
                                    <List>
                                        <ListItem>
                                            If selected, users within this
                                            company can grant access to 7DAY
                                            using email addresses that are not
                                            part of their organisation (for
                                            example, personal email accounts).
                                            If not selected, only email
                                            addresses belonging to the
                                            organisation may be added.
                                        </ListItem>
                                    </List>
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    </Stack>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Controller
                        name='company.allowsMultipleDomains'
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                checked={!!field.value}
                                onChange={(e) =>
                                    field.onChange(e.target.checked)
                                }
                                disabled={!canEditMultipleDomains}
                                sx={{ p: 0 }}
                            />
                        )}
                    />
                </Grid>
            </Grid>

            {!isEditing && (
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel>Address</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <TextField
                            size='small'
                            fullWidth
                            value={simplifiedAddress}
                            disabled
                        />
                    </Grid>
                </Grid>
            )}

            {canEditCompanyDetails && (
                <>
                    <Grid item xs={12} />
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <InputLabel>Street Address</InputLabel>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormTextField
                                name='company.streetAddress'
                                control={control}
                            />
                        </Grid>
                    </Grid>

                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <InputLabel>Suburb</InputLabel>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormTextField
                                name='company.suburb'
                                control={control}
                            />
                        </Grid>
                    </Grid>

                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <InputLabel>Post Code</InputLabel>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormTextField
                                type='number'
                                name='company.postcode'
                                control={control}
                            />
                        </Grid>
                    </Grid>

                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <InputLabel>State</InputLabel>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormAutocomplete
                                name='company.state'
                                control={control}
                                options={[
                                    { value: 'NSW', label: 'NSW' },
                                    { value: 'QLD', label: 'QLD' },
                                    { value: 'VIC', label: 'VIC' },
                                    { value: 'SA', label: 'SA' },
                                    { value: 'WA', label: 'WA' },
                                    { value: 'NT', label: 'NT' },
                                    { value: 'TAS', label: 'TAS' },
                                    { value: 'ACT', label: 'ACT' },
                                ]}
                            />
                        </Grid>
                    </Grid>

                    <Grid container item xs={12} lg={6}>
                        <Grid item xs={12} lg={5}>
                            <InputLabel required>Country</InputLabel>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormAutocomplete
                                name='company.countryCode'
                                control={control}
                                options={[{ value: 'AU', label: 'Australia' }]}
                            />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} />
                </>
            )}

            {isCompany && !isEditing ? (
                <>
                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <Stack
                                direction='row'
                                gap='5px'
                                alignItems='center'
                            >
                                <InputLabel>Status</InputLabel>
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    Enabled: Users can access
                                                    the site and perform actions
                                                    based on their level of
                                                    access
                                                </ListItem>
                                                <ListItem>
                                                    Disabled: Users will not be
                                                    able to access the site
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <FormSelect
                                name='company.isActive'
                                control={control}
                                options={[
                                    {
                                        value: true,
                                        label: 'Enabled',
                                        iconLeft: <ProfileStatusDot enabled />,
                                    },
                                    {
                                        value: false,
                                        label: 'Disabled',
                                        iconLeft: (
                                            <ProfileStatusDot enabled={false} />
                                        ),
                                    },
                                ]}
                                disabled
                            />
                        </Grid>
                    </Grid>

                    <Grid item container xs={12} md={6}>
                        <Grid item xs={12} lg={5}>
                            <Stack
                                direction='row'
                                gap='5px'
                                alignItems='center'
                            >
                                <InputLabel>Access</InputLabel>
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            <List>
                                                <ListItem>
                                                    Master Account:
                                                    Add/Edit/View Users
                                                </ListItem>
                                                <ListItem>
                                                    Admin: Add/Edit/View
                                                    Listings, Users and
                                                    Reservations
                                                </ListItem>
                                                <ListItem>
                                                    User: Add/Edit/View
                                                    Reservations for themselves
                                                    & view listings
                                                </ListItem>
                                                <ListItem>
                                                    View Only User: View
                                                    listings & view reservations
                                                    made for them
                                                </ListItem>
                                            </List>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} lg={7}>
                            <TextField
                                size='small'
                                fullWidth
                                value={
                                    loggedInUser?.role === 'owner_admin'
                                        ? 'Admin'
                                        : loggedInUser?.role === 'owner_user'
                                          ? 'User'
                                          : loggedInUser?.role === 'guest_user'
                                            ? loggedInUser?.viewOnly
                                                ? 'View Only User'
                                                : 'User'
                                            : loggedInUser?.role === 'admin'
                                              ? 'Admin'
                                              : 'Master Account'
                                }
                                disabled
                            />
                        </Grid>
                    </Grid>
                </>
            ) : null}
        </Grid>
    );

    return (
        <>
            {shell ? (
                <ProfileSectionCard
                    icon={<BusinessIcon sx={{ fontSize: 22 }} />}
                    title='Company Details'
                    subtitle='Information about your company and primary contact.'
                    editButtonLabel='Edit Company'
                    onEdit={() => beginInlineEdit('company')}
                    showEditButton={canShowCompanyInlineEdit}
                    inlineEditActive={inlineEditSection === 'company'}
                    onCancelInline={cancelInlineEdit}
                    onSaveInline={saveInlineEdit}
                    saveDisabled={inlineEditSection === 'company' && !isDirty}
                >
                    {canViewCompanyReviews && loggedInUser?.company ? (
                        <GuestPublicProfileRow
                            averageRating={guestCompanyProfile?.averageRating}
                            reviewCount={guestCompanyProfile?.reviewCount}
                            to={`/guests/${loggedInUser.company}`}
                        />
                    ) : null}
                    {readOnlyShell
                        ? guestCompanyReadOnly
                        : guestCompanyShellEdit}
                </ProfileSectionCard>
            ) : (
                <>
                    <Typography
                        component='h4'
                        sx={{
                            color: 'text.primary',
                            fontWeight: 'bold',
                            py: '30px',
                        }}
                    >
                        Company Details
                    </Typography>
                    {companyFieldsGrid}
                </>
            )}
        </>
    );
}
